import type { ReactNode } from 'react';

export type HistoryTab = 'scan' | 'batch';

interface HistoryPageLayoutProps {
  activeTab: HistoryTab;
  onTabChange?: (tab: HistoryTab) => void;
  scanLabel?: string;
  batchLabel?: string;
  description?: string;
  showBatchTab?: boolean;
  children: ReactNode;
}

export function HistoryPageLayout({
  activeTab,
  onTabChange,
  scanLabel = 'Scan History',
  batchLabel = 'Batch History',
  description,
  showBatchTab = true,
  children,
}: HistoryPageLayoutProps) {
  const tabs = [
    { id: 'scan' as const, label: scanLabel },
    ...(showBatchTab ? [{ id: 'batch' as const, label: batchLabel }] : []),
  ];

  return (
    <div className="space-y-4">
      <header className="border-b border-slate-200">
        <div className="flex min-h-11 items-end gap-7" role="tablist" aria-label="History views">
          {tabs.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onTabChange?.(tab.id)}
                className={`relative pb-2.5 font-medium transition-colors ${
                  active
                    ? 'text-xl tracking-tight text-slate-950 sm:text-2xl'
                    : 'text-xs text-slate-500 hover:text-slate-800'
                }`}
              >
                {tab.label}
                {active && (
                  <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-t bg-indigo-600" />
                )}
              </button>
            );
          })}
        </div>
        {description && <p className="pb-3 text-xs text-slate-500">{description}</p>}
      </header>

      {children}
    </div>
  );
}
